import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { chartTheme, fmtMoney, fmtSignedMoney, fmtPct, fmtNum, fmtDurationHM, pnlClass } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Panel({ title, right, children, className, testId }) {
  return (
    <div className={cn("rounded-2xl border bg-card p-4", className)} data-testid={testId}>
      {(title || right) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title && <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function Metric({ label, value, cls }) {
  return (
    <div>
      <div className={cn("text-sm font-semibold tabular-nums", cls)}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

export function GeneralInfo({ stats, currency }) {
  const strip = stats?.strip || {};
  const returns = stats?.returns || {};
  const volume = stats?.volume || {};
  return (
    <Panel title="General info" testId="overview-general-info">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Metric label="Gain" value={fmtPct(strip.gain_pct, true)} cls={pnlClass(strip.gain_pct)} />
          <Metric label="Abs. gain" value={fmtSignedMoney(strip.abs_gain, currency)} cls={pnlClass(strip.abs_gain)} />
          <Metric label="Daily" value={fmtPct(returns.avg_daily_pct, true)} cls={pnlClass(returns.avg_daily_pct)} />
          <Metric label="Monthly" value={fmtPct(returns.avg_monthly_pct, true)} cls={pnlClass(returns.avg_monthly_pct)} />
          <Metric label="Drawdown" value={`-${fmtPct(strip.max_drawdown_pct)}`} cls="text-[hsl(var(--loss))]" />
        </div>
        <div className="grid grid-cols-2 gap-3 border-t pt-3">
          <Metric label="Balance" value={fmtMoney(strip.balance, currency)} />
          <Metric label="Equity" value={fmtMoney(strip.equity, currency)} />
        </div>
        <div className="border-t pt-3">
          <Metric label="Profit" value={fmtSignedMoney(strip.abs_gain, currency)} cls={pnlClass(strip.abs_gain)} />
        </div>
        <div className="grid grid-cols-2 gap-3 border-t pt-3">
          <Metric label="Deposits" value={fmtMoney(volume.deposits, currency)} />
          <Metric label="Withdrawals" value={fmtMoney(volume.withdrawals, currency)} />
        </div>
      </div>
    </Panel>
  );
}

export function EquityPanel({ curve, currency, selectedLabel, onClear }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const points = (curve || []).map((p) => ({ ...p, dateLabel: p.date.slice(5) }));
  return (
    <Panel title="Equity curve (realized)" testId="overview-equity-panel" className="lg:col-span-2"
      right={selectedLabel && (
        <button type="button" onClick={onClear} data-testid="equity-panel-clear-month"
          className="flex items-center gap-1 rounded-full bg-surface-2 px-2.5 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:text-foreground">
          {selectedLabel} <X className="h-3 w-3" />
        </button>
      )}>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={points} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="overviewEquityFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={t.areaFill} stopOpacity={0.28} />
              <stop offset="100%" stopColor={t.areaFill} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} minTickGap={40} />
          <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={52}
            tickFormatter={(v) => fmtMoney(v, currency, true)} domain={["auto", "auto"]} />
          <Tooltip formatter={(v) => fmtMoney(v, currency)} labelStyle={{ color: "inherit" }}
            contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Area type="monotone" dataKey="balance" stroke={t.area} strokeWidth={2} fill="url(#overviewEquityFill)" dot={false} name="Balance" />
        </AreaChart>
      </ResponsiveContainer>
    </Panel>
  );
}

export function StrategyTable({ rows, currency }) {
  const list = rows || [];
  const totalTrades = list.reduce((a, r) => a + r.trades, 0);
  const totalPl = list.reduce((a, r) => a + r.net_profit, 0);
  const totalWinRate = totalTrades ? list.reduce((a, r) => a + (r.win_rate * r.trades) / 100, 0) / totalTrades * 100 : 0;
  return (
    <Panel title="Strategy breakdown" testId="overview-strategy-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase text-muted-foreground">
            <th className="pb-2">Magic</th><th className="pb-2">Strategy</th>
            <th className="pb-2 text-right">Win%</th><th className="pb-2 text-right">Trades</th><th className="pb-2 text-right">P/L</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.magic} className="border-t" data-testid={`overview-strategy-row-${r.magic}`}>
              <td className="py-1.5">{r.magic}</td>
              <td className="py-1.5">{r.name}</td>
              <td className="py-1.5 text-right tabular-nums">{fmtPct(r.win_rate)}</td>
              <td className="py-1.5 text-right tabular-nums">{r.trades}</td>
              <td className={cn("py-1.5 text-right tabular-nums font-medium", pnlClass(r.net_profit))}>{fmtSignedMoney(r.net_profit, currency)}</td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">No trades yet.</td></tr>
          )}
        </tbody>
        {list.length > 0 && (
          <tfoot>
            <tr className="border-t font-semibold">
              <td className="py-1.5" colSpan={2}>Total</td>
              <td className="py-1.5 text-right tabular-nums">{fmtPct(totalWinRate)}</td>
              <td className="py-1.5 text-right tabular-nums">{totalTrades}</td>
              <td className={cn("py-1.5 text-right tabular-nums", pnlClass(totalPl))}>{fmtSignedMoney(totalPl, currency)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </Panel>
  );
}

export function SymbolBars({ symbols, currency }) {
  const list = symbols || [];
  const max = Math.max(1, ...list.map((s) => Math.abs(s.profit)));
  const profitable = list.filter((s) => s.profit >= 0).length;
  return (
    <Panel title={`P/L by symbol (${list.length} traded)`} testId="overview-symbol-bars">
      <div className="space-y-2">
        {list.slice(0, 8).map((s) => (
          <div key={s.symbol} className="flex items-center gap-2 text-xs" data-testid={`overview-symbol-${s.symbol}`}>
            <span className="w-16 shrink-0 font-medium">{s.symbol}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
              <div className={cn("h-full rounded-full", s.profit >= 0 ? "bg-[hsl(var(--profit))]" : "bg-[hsl(var(--loss))]")}
                style={{ width: `${(Math.abs(s.profit) / max) * 100}%` }} />
            </div>
            <span className={cn("w-20 shrink-0 text-right tabular-nums font-medium", pnlClass(s.profit))}>
              {fmtSignedMoney(s.profit, currency)}
            </span>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-muted-foreground">No trades in this period.</p>}
      </div>
      {list.length > 0 && (
        <p className="mt-3 text-[11px] text-muted-foreground">{profitable} profitable · {list.length - profitable} losing</p>
      )}
    </Panel>
  );
}

export function SessionsPanel({ sessions }) {
  const list = sessions || [];
  const ranges = { Asia: "00-08", London: "08-13", "New York": "13-22" };
  const max = Math.max(1, ...list.map((s) => Math.abs(s.profit)));
  return (
    <Panel title="Sessions (broker GMT+3)" testId="overview-sessions-panel">
      <div className="space-y-3">
        {list.map((s) => (
          <div key={s.session} data-testid={`overview-session-${s.session.replace(" ", "-")}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{s.session} <span className="text-muted-foreground">{ranges[s.session]}</span></span>
              <span className={cn("font-semibold tabular-nums", pnlClass(s.profit))}>{fmtSignedMoney(s.profit)}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2">
              <div className={cn("h-full rounded-full", s.profit >= 0 ? "bg-[hsl(var(--profit))]" : "bg-[hsl(var(--loss))]")}
                style={{ width: `${(Math.abs(s.profit) / max) * 100}%` }} />
            </div>
            <div className="mt-0.5 text-[10px] text-muted-foreground">{s.trades} trades · {fmtPct(s.win_rate)} win</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function HourlyChart({ hours }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const rows = (hours || []).map((h) => ({ label: `${h.hour}h`, ...h }));
  return (
    <Panel title="Profit by hour (green = win, red = loss)" testId="overview-hourly-chart">
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: t.axis }} tickLine={false} axisLine={false} interval={2} />
          <YAxis tick={{ fontSize: 10, fill: t.axis }} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => fmtMoney(v, "USD", true)} />
          <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Bar dataKey="profit" radius={[3, 3, 0, 0]} name="Profit">
            {rows.map((r, i) => <Cell key={i} fill={r.profit >= 0 ? t.profit : t.loss} fillOpacity={0.85} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Panel>
  );
}

export function YearlyPanel({ months, years, selectedYear, onPrevYear, onNextYear, currency }) {
  const { theme } = useTheme();
  const t = chartTheme(theme === "dark");
  const rows = (months || []).map((m) => ({ ...m, label: MONTH_NAMES[m.month - 1] }));
  const yearTotal = (months || []).reduce((a, m) => a + m.profit, 0);
  const yearTrades = (months || []).reduce((a, m) => a + m.trades, 0);
  const yearEntry = (years || []).find((y) => Number(y.year) === selectedYear);
  return (
    <Panel testId="overview-yearly-panel">
      <div className="mb-1 flex items-center justify-between">
        <button type="button" onClick={onPrevYear} data-testid="yearly-prev" className="rounded p-1 hover:bg-surface-2">
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Monthly {selectedYear}</div>
        <button type="button" onClick={onNextYear} data-testid="yearly-next" className="rounded p-1 hover:bg-surface-2">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className={cn("text-sm font-semibold tabular-nums", pnlClass(yearTotal))}>
        {fmtSignedMoney(yearTotal, currency)} <span className="text-xs font-normal text-muted-foreground">{yearTrades} trades</span>
      </div>
      <ResponsiveContainer width="100%" height={90}>
        <BarChart data={rows} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
          <Bar dataKey="profit" radius={[2, 2, 0, 0]}>
            {rows.map((r, i) => <Cell key={i} fill={r.profit >= 0 ? t.profit : t.loss} fillOpacity={0.85} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-3 border-t pt-3">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">P/L by year</div>
        <div className={cn("text-sm font-semibold tabular-nums", pnlClass(yearEntry?.profit))}>
          {fmtSignedMoney(yearEntry?.profit || 0, currency)}
        </div>
      </div>
    </Panel>
  );
}

export function PerformanceDetail({ detail, currency }) {
  const d = detail || {};
  return (
    <Panel title="Performance detail" testId="overview-performance-detail">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Metric label="Gain %" value={fmtPct(d.gain_pct, true)} cls={pnlClass(d.gain_pct)} />
        <Metric label="Highest balance" value={fmtMoney(d.highest_balance, currency)} />
        <Metric label="Total lots" value={fmtNum(d.total_lots, 2)} />
        <Metric label="Std deviation" value={fmtMoney(d.std_deviation, currency)} />
        <Metric label="Avg trade duration" value={fmtDurationHM(d.avg_trade_duration_minutes)} />
      </div>
    </Panel>
  );
}
